import { useState } from "react";
import { COMPANY_INFO } from "../../../mocks/aegis-data";

const FORM_URL = "https://readdy.ai/api/form/d71vb6hlum2co2q1fv10";

interface FinalCTAProps {
  title: string;
  subtitle: string;
  ctaLabel?: string;
}

export default function FinalCTA({ title, subtitle, ctaLabel = "Start Your Project" }: FinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(e.currentTarget);
    formData.forEach((value, key) => data.append(key, value as string));
    try {
      await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  return (
    <section className="relative overflow-hidden">
      {/* Full-bleed cinematic background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("https://public.readdy.ai/ai/img_res/6213cea2ee1d8d9a179f9c135a409472.jpg")',
        }}
      />
      {/* Deep dark overlay */}
      <div className="absolute inset-0 bg-aegisDark/93" />
      {/* Gold left edge */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-aegisGold to-transparent hidden lg:block" />
      {/* Top gold rule */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-aegisGold via-aegisGold/30 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-8 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left: CTA content */}
          <div className="lg:pr-8">
            <div className="flex items-center gap-4 mb-7">
              <div className="h-px w-10 bg-aegisGold" />
              <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">Ready to Build?</span>
            </div>

            <h2 className="font-cormorant font-semibold text-white leading-[1.05] mb-7" style={{ fontSize: "clamp(2.4rem, 4.5vw, 4.5rem)" }}>
              {title}
            </h2>

            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-0.5 bg-aegisGold" />
              <div className="w-2 h-2 border border-aegisGold rotate-45 flex-shrink-0" />
            </div>

            <p className="font-inter text-white/55 text-lg leading-relaxed mb-14">
              {subtitle}
            </p>

            {/* Phone */}
            <a
              href={COMPANY_INFO.phoneHref}
              className="group flex items-center gap-5 mb-5 cursor-pointer"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-aegisGold flex-shrink-0 group-hover:bg-amber-600 transition-colors duration-200">
                <i className="ri-phone-fill text-white text-xl" />
              </div>
              <div>
                <p className="font-inter text-white/35 text-xs tracking-widest uppercase mb-1">Call Us Directly</p>
                <p className="font-cormorant font-semibold text-white leading-none group-hover:text-aegisGold transition-colors duration-200" style={{ fontSize: "2rem" }}>
                  {COMPANY_INFO.phone}
                </p>
              </div>
            </a>

            {/* Email */}
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="group flex items-center gap-5 mb-14 cursor-pointer"
            >
              <div className="w-14 h-14 flex items-center justify-center border border-white/15 group-hover:border-aegisGold transition-colors duration-200 flex-shrink-0">
                <i className="ri-mail-line text-white/50 group-hover:text-aegisGold text-xl" />
              </div>
              <div>
                <p className="font-inter text-white/35 text-xs tracking-widest uppercase mb-1">Email Us</p>
                <p className="font-inter text-white/70 text-sm group-hover:text-white transition-colors duration-200">
                  {COMPANY_INFO.email}
                </p>
              </div>
            </a>

            {/* Trust micro-badges */}
            <div className="space-y-3 pt-8 border-t border-white/10">
              {[
                { icon: "ri-shield-check-line", text: "CGC1524141 + CFC1430823 — Licensed & Insured" },
                { icon: "ri-time-line", text: "We respond within 24 hours — guaranteed" },
                { icon: "ri-hand-coin-line", text: "No obligation · Free project assessment" },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${b.icon} text-aegisGold text-sm`} />
                  </div>
                  <span className="font-inter text-white/40 text-sm">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Lead form */}
          <div className="bg-white">
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center py-20 px-8">
                <div className="w-16 h-16 flex items-center justify-center bg-aegisCream border border-aegisGold mb-7">
                  <i className="ri-check-line text-aegisGold text-2xl" />
                </div>
                <h3 className="font-cormorant font-semibold text-aegisDark text-2xl mb-3">
                  Message Received
                </h3>
                <p className="font-inter text-aegisMuted text-sm leading-relaxed max-w-xs">
                  We&apos;ll review your project and be in touch within 24 hours with honest guidance.
                </p>
              </div>
            ) : (
              <div className="p-8 lg:p-10">
                {/* Form header */}
                <div className="flex items-start justify-between mb-8">
                  <div>
                    <h3 className="font-cormorant font-semibold text-aegisDark text-2xl mb-1">{ctaLabel}</h3>
                    <p className="font-inter text-aegisMuted text-sm">No obligation · No pressure · Real guidance</p>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center border border-aegisStone flex-shrink-0">
                    <i className="ri-chat-check-line text-aegisGold text-lg" />
                  </div>
                </div>

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-inter text-xs tracking-wide uppercase text-aegisMuted block mb-1.5">First Name</label>
                      <input
                        type="text" name="first_name" required placeholder="John"
                        className="w-full border border-aegisStone px-4 py-3 font-inter text-sm text-aegisText focus:outline-none focus:border-aegisGold transition-colors bg-aegisCream"
                      />
                    </div>
                    <div>
                      <label className="font-inter text-xs tracking-wide uppercase text-aegisMuted block mb-1.5">Last Name</label>
                      <input
                        type="text" name="last_name" required placeholder="Smith"
                        className="w-full border border-aegisStone px-4 py-3 font-inter text-sm text-aegisText focus:outline-none focus:border-aegisGold transition-colors bg-aegisCream"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-inter text-xs tracking-wide uppercase text-aegisMuted block mb-1.5">Email Address</label>
                    <input
                      type="email" name="email" required placeholder="john@email.com"
                      className="w-full border border-aegisStone px-4 py-3 font-inter text-sm text-aegisText focus:outline-none focus:border-aegisGold transition-colors bg-aegisCream"
                    />
                  </div>

                  <div>
                    <label className="font-inter text-xs tracking-wide uppercase text-aegisMuted block mb-1.5">Phone Number</label>
                    <input
                      type="tel" name="phone" placeholder="(941) 000-0000"
                      className="w-full border border-aegisStone px-4 py-3 font-inter text-sm text-aegisText focus:outline-none focus:border-aegisGold transition-colors bg-aegisCream"
                    />
                  </div>

                  <div>
                    <label className="font-inter text-xs tracking-wide uppercase text-aegisMuted block mb-1.5">Service Needed</label>
                    <select
                      name="service"
                      className="w-full border border-aegisStone px-4 py-3 font-inter text-sm text-aegisText focus:outline-none focus:border-aegisGold transition-colors bg-aegisCream appearance-none cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option>Custom Home Building</option>
                      <option>Luxury Home Remodeling</option>
                      <option>Shell Construction</option>
                      <option>Coastal &amp; Flood Zone Construction</option>
                      <option>FEMA Elevation &amp; Repairs</option>
                      <option>Foundation &amp; Helical Piles</option>
                      <option>Licensed Plumbing</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-inter text-xs tracking-wide uppercase text-aegisMuted block mb-1.5">Project Details</label>
                    <textarea
                      name="message" rows={4} maxLength={500}
                      placeholder="Tell us about your project, location, and timeline..."
                      className="w-full border border-aegisStone px-4 py-3 font-inter text-sm text-aegisText focus:outline-none focus:border-aegisGold transition-colors bg-aegisCream resize-none"
                    />
                  </div>

                  <button
                    type="submit" disabled={submitting}
                    className="w-full bg-aegisDark text-white font-inter font-semibold text-sm tracking-widest uppercase px-8 py-4 cursor-pointer hover:bg-aegisGold transition-colors duration-300 whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-3"
                  >
                    {submitting ? "Sending..." : (
                      <>
                        {ctaLabel}
                        <i className="ri-arrow-right-long-line" />
                      </>
                    )}
                  </button>

                  <p className="font-inter text-aegisMuted text-xs text-center">
                    CGC1524141 · Licensed &amp; Insured · Sarasota, FL
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
